import React from 'react';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
      </svg>
    ),
    title: 'Simplicity first',
    desc: 'We believe great tools get out of your way. Every feature is designed to reduce friction, not add it.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
    title: 'Built for teams',
    desc: 'From solo developers to large squads, our role-based access and collaboration features scale with you.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
      </svg>
    ),
    title: 'Security & privacy',
    desc: 'Your data belongs to you. Row-level security ensures each company sees only its own data — always.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: 'Always improving',
    desc: 'We ship updates continuously based on real user feedback. Your voice shapes what we build next.',
  },
];

const stats = [
  { value: '100%', label: 'Free to use' },
  { value: '6', label: 'Core features' },
  { value: '3', label: 'User roles' },
  { value: '∞', label: 'Projects & issues' },
];

export default function AboutPage() {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="landing-container">
          <div className="help-hero-back">
            <Link to="/">← Back to home</Link>
          </div>
          <h1 className="about-hero-title">
            Built for agile teams.<br />
            <span className="landing-headline-accent">Designed to ship faster.</span>
          </h1>
          <p className="about-hero-sub">
            Naipeak Issue Tracker was created to give software teams a clean, focused tool for bug tracking and project management — without the bloat of enterprise platforms.
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <section className="about-stats-bar">
        <div className="landing-container">
          <div className="about-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="about-stat">
                <span className="about-stat-value">{s.value}</span>
                <span className="about-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="landing-section about-mission-section">
        <div className="landing-container about-mission-inner">
          <div className="about-mission-text">
            <h2 className="about-section-title">Our mission</h2>
            <p>
              Software teams spend too much time managing their tools instead of managing their software.
              We built Naipeak Issue Tracker to fix that — a platform that feels fast on day one and stays
              fast as your team grows.
            </p>
            <p>
              Every decision — from the role-based access model to the real-time notification system — is
              made with one goal in mind: help your team ship better software, faster.
            </p>
            <p>
              Whether you're a startup with two developers or a company with a full QA team, Naipeak Issue
              Tracker provides the structure you need without forcing a process that doesn't fit.
            </p>
            <Link to="/signup" className="landing-btn-primary" style={{ display: 'inline-flex', marginTop: 8 }}>
              Start for free
            </Link>
          </div>
          <div className="about-mission-visual" aria-hidden="true">
            <div className="about-visual-card">
              <div className="about-visual-header">
                <svg viewBox="0 0 32 32" width="28" height="28">
                  <defs>
                    <linearGradient id="av-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1C9B82"/><stop offset="100%" stopColor="#00796B"/>
                    </linearGradient>
                  </defs>
                  <rect width="32" height="32" rx="7" fill="url(#av-grad)"/>
                  <polyline points="7.5,17 13,22.5 24.5,10.5" fill="none" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Naipeak Issue Tracker</span>
              </div>
              {[
                { label: 'Open Issues', value: '12', color: 'var(--color-open)' },
                { label: 'In Progress', value: '7', color: 'var(--color-progress)' },
                { label: 'Closed Today', value: '5', color: 'var(--color-success)' },
                { label: 'Team Members', value: '8', color: 'var(--color-primary)' },
              ].map((row, i) => (
                <div key={i} className="about-visual-row">
                  <span className="about-visual-label">{row.label}</span>
                  <span className="about-visual-value" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="landing-section about-values-section">
        <div className="landing-container">
          <h2 className="landing-section-title">What we stand for</h2>
          <p className="landing-section-sub">The principles behind every decision we make.</p>
          <div className="landing-features-grid">
            {values.map((v, i) => (
              <div key={i} className="landing-feature-card">
                <div className="landing-feature-icon">{v.icon}</div>
                <h3 className="landing-feature-title">{v.title}</h3>
                <p className="landing-feature-body">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Naipeak ICT section */}
      <section className="about-powered-section">
        <div className="landing-container">
          <div className="about-powered-card">
            <div className="about-powered-logo">
              <svg viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
                <defs>
                  <linearGradient id="np-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1C9B82"/><stop offset="100%" stopColor="#00796B"/>
                  </linearGradient>
                </defs>
                <rect width="48" height="48" rx="12" fill="url(#np-grad)"/>
                <polyline points="11,25.5 19.5,34 37,15.5" fill="none" stroke="white" strokeWidth="4.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="about-powered-content">
              <div className="about-powered-label">Powered by</div>
              <h3 className="about-powered-name">Naipeak ICT</h3>
              <p className="about-powered-desc">
                Naipeak Issue Tracker is a product of <strong>Naipeak ICT</strong> — a technology company building
                practical digital tools for modern businesses. Naipeak ICT develops software solutions that solve
                real problems for real teams, without unnecessary complexity.
              </p>
              <a
                href="https://naipeak.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-btn-secondary"
                style={{ display: 'inline-flex', marginTop: 4 }}
              >
                Visit Naipeak ICT →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta-banner">
        <div className="landing-container landing-cta-inner">
          <h2 className="landing-cta-title">Ready to track your first bug?</h2>
          <p className="landing-cta-body">Join agile teams using Naipeak Issue Tracker to ship better software, faster. Free forever.</p>
          <div className="landing-cta-group">
            <Link to="/signup" className="landing-btn-primary">Get started free</Link>
            <Link to="/help" className="landing-btn-ghost">Read the docs</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
